import * as React from 'react';
import { TextField, styled, TextFieldProps, Theme, Paper } from '@mui/material';
import { DateTimePicker, DateTimePickerProps } from 'formik-mui-lab';

import { fontFamily, weights } from '../context/CerebroThemeProvider';
import { MUIStyledCommonProps } from '@mui/system';

type StyledTextFieldProps = TextFieldProps & MUIStyledCommonProps & { theme: Theme; readOnly: boolean };

const StyledTextField = styled(TextField)(({ error, disabled, readOnly, theme }: StyledTextFieldProps) => ({
	backgroundColor: theme.palette.gray.background,

	borderWidth: 2,
	borderStyle: 'solid',
	borderRadius: theme.shape.borderRadius,
	borderColor: error
		? theme.palette.error.main
		: disabled || readOnly
		? theme.palette.gray.light
		: theme.palette.gray.medium,

	color: disabled ? theme.palette.gray.light : theme.palette.gray.dark,

	'&, & *': {
		cursor: disabled ? 'not-allowed' : 'initial',
	},

	height: 46,

	marginTop: theme.spacing(1.5),
	marginBottom: theme.spacing(1),

	outline: 'none',

	width: '100%',

	'&:focus-within': {
		borderColor: error
			? theme.palette.error.main
			: readOnly
			? theme.palette.gray.light
			: theme.palette.primary.main,
	},

	'& input': {
		fontFamily,
		fontSize: 12,
		fontWeight: disabled ? weights.medium : weights.semibold,
		lineHeight: '14px',

		padding: '14px 12px',
	},

	'& fieldset': {
		border: 'none',
	},

	'& .MuiInputAdornment-root *': {
		borderRadius: '0 !important',
	},

	'&.open .MuiInputAdornment-root button': {
		backgroundColor: theme.palette.primary.main,
	},

	'& .MuiInputAdornment-root svg': {
		color: disabled || readOnly ? theme.palette.gray.light : theme.palette.gray.dark,
	},

	'& .MuiPaper-root': {
		backgroundColor: theme.palette.gray.background,
		border: `1px solid ${theme.palette.gray.dark}`,
	},

	'& .MuiPickersDay-root': {
		borderRadius: 0,
	},

	'& div[role="dialog"]': {
		paddingTop: '8px !important',
		paddingBottom: '8px !important',
	},

	'& .MuiPickersDay-root:focus.Mui-selected': {
		backgroundColor: theme.palette.primary.light,
	},
})) as any as typeof TextField;

type CerebroDateTimePickerProps = Omit<DateTimePickerProps, 'open' | 'onOpen' | 'onClose' | 'renderInput'> & {
	error: boolean;
};

export const CerebroDateTimePicker: React.FC<CerebroDateTimePickerProps> = ({ error, ...props }) => {
	const [open, setOpen] = React.useState(false);
	const ref = React.useRef<HTMLInputElement | null>(null);

	return (
		<DateTimePicker
			{...props}
			inputRef={ref}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			renderInput={params => (
				<CerebroDateTimePickerInput
					{...params}
					type="datetime-local"
					error={error}
					open={open}
					readOnly={props.readOnly}
				/>
			)}
			PopperProps={{
				container: ref.current?.parentElement,
				placement: 'bottom-end',
			}}
		/>
	);
};

const CerebroDateTimePickerInput: React.FC<TextFieldProps & { open: boolean; readOnly?: boolean }> = ({
	open,
	...props
}) => {
	let className = props.className ?? '';

	if (open) {
		className = `${className} open`;
	}

	return <StyledTextField {...props} className={className} />;
};
